import { useState } from "react";
import axios from "axios";
import { Check } from "lucide-react";
import Navigation from "@/components/Navigation";
import ChatbotWidget from "@/components/ChatbotWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/assets/logo.png";

const PARTNERSHIP_TYPES = [
  "Luxury Brand",
  "Hospitality Partner",
  "Artist / Management",
  "Sponsorship",
  "Media / Press",
  "Other",
];

export default function PartnershipPage() {
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    partnership_type: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company.trim() || !form.contact_name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setErrorMsg("Please fill in all required fields.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/partnership`, form);
      setStatus("success");
      setForm({ company: "", contact_name: "", email: "", partnership_type: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <>
      <div className="star-bg-fixed" aria-hidden="true" />
      <div className="star-tint min-h-screen relative">
        <Navigation />

        <section
          id="partnership"
          className="pt-40 pb-24 sm:pt-48 sm:pb-32 border-b border-white/5"
          data-testid="partnership-section"
        >
          <div className="max-w-5xl mx-auto px-6 sm:px-12 text-center">
            <img src={LOGO} alt="AirStageX" className="h-16 sm:h-20 mx-auto mb-10 opacity-95" />
            <p className="overline text-[#007AFF] mb-6 drift">Partner with AirStageX</p>
            <h1 className="font-heading font-light text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] mb-7">
              Be Part of the World&rsquo;s Most Extraordinary Stage
            </h1>
            <div className="text-zinc-200 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto space-y-6 text-left sm:text-center">
              <p>
                AirStageX is creating an entirely new category at the intersection of private aviation,
                luxury hospitality, live entertainment, and global brand experiences.
              </p>
              <p>
                We will be partnering with a carefully selected group of luxury brands, five-star hotels,
                premium lifestyle companies, artists, agencies, and strategic sponsors to deliver
                unforgettable experiences that transcend traditional aviation.
              </p>
              <p>
                For our partners, AirStageX offers more than visibility, it provides access to an affluent
                global audience, meaningful brand integration, and the opportunity to be part of a
                pioneering movement that is redefining luxury travel.
              </p>
              <p>
                If your brand is driven by innovation, exclusivity, and excellence, we invite you to join
                us in shaping the future of luxury in the sky.
              </p>
              <p>
                Ready to explore a partnership with AirStageX? We&rsquo;d be delighted to discuss how we can
                create an extraordinary collaboration that elevates your brand to new heights.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-28" data-testid="partnership-form-section">
          <div className="max-w-3xl mx-auto px-6 sm:px-12">
            {status === "success" ? (
              <div
                className="flex flex-col items-center justify-center text-center py-16"
                data-testid="partnership-success"
              >
                <div className="w-14 h-14 border border-[#007AFF] flex items-center justify-center mb-6 rounded-full">
                  <Check className="w-6 h-6 text-[#007AFF]" />
                </div>
                <h3 className="font-heading text-2xl font-light text-white mb-3">Thank you</h3>
                <p className="text-zinc-300 text-base max-w-md">
                  Your partnership enquiry has been received. Our team will review and reply
                  personally within a few working days.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="space-y-8"
                data-testid="partnership-form"
              >
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Company / Brand *</label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors placeholder:text-zinc-500 w-full text-sm"
                    placeholder="Your company or brand name"
                    required
                    data-testid="partnership-company"
                  />
                </div>
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Contact name *</label>
                  <input
                    type="text"
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors placeholder:text-zinc-500 w-full text-sm"
                    placeholder="Your full name"
                    required
                    data-testid="partnership-contact-name"
                  />
                </div>
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors placeholder:text-zinc-500 w-full text-sm"
                    placeholder="you@company.com"
                    required
                    data-testid="partnership-email"
                  />
                </div>
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Partnership type</label>
                  <select
                    value={form.partnership_type}
                    onChange={(e) => setForm({ ...form, partnership_type: e.target.value })}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors w-full text-sm appearance-none cursor-pointer"
                    data-testid="partnership-type"
                  >
                    <option value="" className="bg-[#0A0A0A]">Select a partnership type</option>
                    {PARTNERSHIP_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-[#0A0A0A]">{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="overline text-zinc-300 mb-2 block">Message *</label>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={5}
                    className="bg-transparent border-b border-white/30 px-0 py-3 text-white focus:outline-none focus:border-[#007AFF] transition-colors placeholder:text-zinc-500 w-full text-sm resize-y"
                    placeholder="Tell us a little about your brand and what you have in mind."
                    required
                    data-testid="partnership-message"
                  />
                </div>
                {status === "error" && (
                  <p className="text-red-400 text-sm" data-testid="partnership-error">{errorMsg}</p>
                )}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="btn-pill btn-pill-primary w-full disabled:opacity-50"
                  data-testid="partnership-submit"
                >
                  {status === "loading" ? "Submitting..." : "Send Partnership Enquiry"}
                </button>
              </form>
            )}
          </div>
        </section>

        <footer className="py-12 border-t border-white/5" data-testid="partnership-footer">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-zinc-400 text-xs">All rights reserved &copy; 2025 AirStageX</p>
            <a href="/" className="text-zinc-300 text-xs hover:text-white transition-colors" data-testid="partnership-back-home">
              Back to home
            </a>
          </div>
        </footer>

        <ChatbotWidget />
      </div>
    </>
  );
}
